import { components } from "@/api-schema";
import { VehicleSearchBody } from "@/common/api/vehicle/use-vehicle-search.query";
import { useDevice } from "@/common/hooks/use-device";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { VehicleTable } from "@/components/data-table/predefined/vehicle-table";
import { vehicleTableDefaultColumns } from "@/components/data-table/predefined/vehicle-table-columns";
import { DataTableRow, ImplementedColumnDef } from "@/components/data-table/types";
import { BranchFilter } from "@/components/filter/predefined/branch-filter";
import { BrandFilter } from "@/components/filter/predefined/brand-filter";
import { ModelGenerationFilter } from "@/components/filter/predefined/model-generation-filter";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { MapPinPenIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { VehicleSubComponent } from "./_components/vehicle-sub-component";

type Vehicle = components["schemas"]["Vehicle"] & RowWithIdentifier;

const RELOCATION_ORDER_INITIALIZE_VEHICLE_COLUMNS = [
  vehicleTableDefaultColumns.mobile.columnMobileCompleteWithoutTransportStatus,
  vehicleTableDefaultColumns.desktopAndTablet.columnBrandModelDescription,
  vehicleTableDefaultColumns.desktopAndTablet.columnVinColor,
  vehicleTableDefaultColumns.widescreen.columnBrand,
  vehicleTableDefaultColumns.widescreen.columnModelDescription,
  vehicleTableDefaultColumns.widescreen.columnVin,
  vehicleTableDefaultColumns.widescreen.columnColor,
  vehicleTableDefaultColumns.widescreenDesktopAndTablet.columnOwningBranch,
  vehicleTableDefaultColumns.widescreenDesktopAndTablet.columnCurrentBranch,
] as ImplementedColumnDef<Vehicle, unknown>[];

export function AdminSelectVehicleView() {
  const { t } = useTranslation(["common", "fit"]);
  const [_, navigate] = useLocation();
  const { isMobileOrTablet } = useDevice();

  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({
    branches: [],
    brands: [],
    modelGenerations: [],
  });

  const baseBody = useMemo((): VehicleSearchBody => {
    return {
      currentBranch: filterValues.branches?.length > 0 ? filterValues.branches : [],
      onlyAvailableForRelocation: true,
      isBlocked: false,
      ...(filterValues.brands?.length > 0 && { brand: filterValues.brands }),
      ...(filterValues.modelGenerations?.length > 0 && { modelGeneration: filterValues.modelGenerations }),
    };
  }, [filterValues.branches, filterValues.brands, filterValues.modelGenerations]);

  const filters = useMemo(
    () => [
      new BranchFilter().setLabelOverrides({
        labelSingular: t("common:Location"),
        labelPlural: t("fit:Locations"),
        placeholder: t("common:SelectLocation"),
        searchPlaceholder: t("common:SearchBranches"),
        emptyText: t("common:NoBranchesFound"),
      }),
      new BrandFilter(),
      new ModelGenerationFilter({ body: { brand: filterValues.brands || [] } }),
    ],
    [filterValues.brands, t],
  );

  const handleFilterChange = useCallback((filterId: string, values: string[]) => {
    setFilterValues((prev) => {
      const newValues = {
        ...prev,
        [filterId]: values,
      };

      if (filterId === "brands") {
        newValues.modelGenerations = [];
      }

      return newValues;
    });
  }, []);

  const navigateToRelocationOrderCreate = useCallback(
    (vehicle: Vehicle) => {
      navigate(`~/fit/relocation-order/create?vehicleUuid=${vehicle.uuid}`);
    },
    [navigate],
  );

  const customRowActions = useCallback(
    (row: DataTableRow<Vehicle>) => {
      return [
        {
          label: t("common:RequestTransport"),
          icon: <MapPinPenIcon className="h-4 w-4" />,
          onClick: () => navigateToRelocationOrderCreate(row.original),
        },
      ];
    },
    [t, navigateToRelocationOrderCreate],
  );

  const handleRowClick = useCallback(
    (vehicle: Vehicle) => {
      navigateToRelocationOrderCreate(vehicle);
    },
    [navigateToRelocationOrderCreate],
  );

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Table}
      title={t("common:SelectVehicle")}
      subTitle={t("fit:SelectVehicleDescription")}
    >
      <VehicleTable
        columns={RELOCATION_ORDER_INITIALIZE_VEHICLE_COLUMNS}
        body={baseBody}
        filters={filters}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
        customRowActions={isMobileOrTablet ? undefined : customRowActions}
        onRowClick={handleRowClick}
        subComponent={VehicleSubComponent}
      />
    </CenteredLayout>
  );
}
